import React from 'react';
import { useNavigate } from 'react-router-dom';
import { whatsappConfig } from '../utils/mockData';

const formatPrice = (amount) => `₹ ${amount.toLocaleString('en-IN')}`;

/**
 * Unified product card used by Collection (archive pieces) and ReadyToBuy.
 * variant = 'collection' → "Inquire" CTA (bespoke)
 * variant = 'ready'      → Shows price + "Order via WhatsApp"
 *
 * The whole card surface is clickable and routes to /archive/:id or /shop/:id,
 * except the explicit CTA button which performs its own action.
 */
const ProductCard = ({ piece, variant = 'collection', onInquire, testIdPrefix = 'pc' }) => {
  const navigate = useNavigate();
  const isReady = variant === 'ready';

  const handleOrder = (e) => {
    e.stopPropagation();
    const message = `Adaab. I would like to order "${piece.name}" (${formatPrice(piece.price)}) from the Silk & Sentiments Ready to Buy collection. Please share availability and next steps.`;
    const url = `https://wa.me/${whatsappConfig.number}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleInquire = (e) => {
    e.stopPropagation();
    onInquire?.(piece);
  };

  const handleOpen = () => {
    let path;
    if (isReady || piece.type === 'ready-to-wear') path = `/shop/${piece.id}`;
    else path = `/archive/${piece.id}`;
    navigate(path);
  };

  return (
    <article
      className="product-card product-card--clickable"
      onClick={handleOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleOpen(); } }}
      role="link"
      tabIndex={0}
      aria-label={`View ${piece.name} details`}
      data-testid={`${testIdPrefix}-card-${piece.id}`}
    >
      <div className="product-card__media">
        <img src={piece.image} alt={piece.name} loading="lazy" />
        {isReady && <span className="product-card__tag">In Stock</span>}
        <div className="product-card__hover">
          {isReady ? (
            <button
              className="product-card__cta product-card__cta--primary"
              onClick={handleOrder}
              data-testid={`${testIdPrefix}-order-btn-${piece.id}`}
            >
              Order via WhatsApp
            </button>
          ) : (
            <button
              className="product-card__cta product-card__cta--ghost"
              onClick={handleInquire}
              data-testid={`${testIdPrefix}-inquire-btn-${piece.id}`}
            >
              Inquire
            </button>
          )}
        </div>
      </div>

      <div className="product-card__body">
        <p className="product-card__technique">{piece.technique}</p>
        <h3 className="product-card__name">{piece.name}</h3>
        {isReady ? (
          <p
            className="product-card__price"
            data-testid={`${testIdPrefix}-price-${piece.id}`}
          >
            {formatPrice(piece.price)}
          </p>
        ) : (
          <p className="product-card__material">
            {piece.material || 'Pure Silk'}
          </p>
        )}
      </div>
    </article>
  );
};

// Scoped styles for the new product card (loaded once)
const STYLE_ID = 'product-card-styles';
if (!document.getElementById(STYLE_ID)) {
  const css = document.createElement('style');
  css.id = STYLE_ID;
  css.textContent = `
    .product-card {
      background: var(--bg-primary);
      border: none;
      border-radius: 0;
      overflow: visible;
      display: flex;
      flex-direction: column;
      box-shadow: none;
      transition: transform 0.4s cubic-bezier(.2,.8,.2,1);
    }
    .product-card:hover {
      transform: translateY(-3px);
    }
    .product-card__media {
      position: relative;
      aspect-ratio: 4 / 5;
      overflow: hidden;
      background: var(--bg-secondary);
      border: 1px solid rgba(188,187,180,0.25);
    }
    .product-card__media img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 1.1s cubic-bezier(.2,.8,.2,1);
    }
    .product-card:hover .product-card__media img {
      transform: scale(1.045);
    }
    .product-card__tag {
      position: absolute;
      top: 10px;
      left: 10px;
      padding: 3px 9px;
      background: rgba(255,254,242,0.94);
      font-family: 'Inter', sans-serif;
      font-size: 9px;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      color: var(--accent-gold);
      border: 1px solid rgba(201,169,97,0.3);
      border-radius: 999px;
      letter-spacing: 0.16em;
    }
    .product-card__hover {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      padding: 14px;
      display: flex;
      justify-content: center;
      transform: translateY(100%);
      transition: transform 0.4s cubic-bezier(.2,.8,.2,1), opacity 0.3s ease;
      opacity: 0;
    }
    .product-card:hover .product-card__hover,
    .product-card:focus-visible .product-card__hover {
      transform: translateY(0);
      opacity: 1;
    }
    .product-card__body {
      padding: 16px 4px 4px;
      display: flex;
      flex-direction: column;
      text-align: left;
    }
    .product-card__technique {
      font-family: 'Inter', sans-serif;
      font-size: 9px;
      letter-spacing: 0.22em;
      text-transform: uppercase;
      color: var(--accent-gold);
      margin: 0 0 8px;
    }
    .product-card__name {
      font-family: 'Playfair Display', serif;
      font-size: 17px;
      font-weight: 400;
      color: var(--text-primary);
      margin: 0 0 6px;
      line-height: 1.3;
      letter-spacing: 0.005em;
    }
    .product-card__material {
      font-family: 'Inter', sans-serif;
      font-size: 11px;
      letter-spacing: 0.05em;
      color: var(--text-secondary);
      margin: 0;
      font-style: italic;
    }
    .product-card__price {
      font-family: 'Playfair Display', serif;
      font-size: 15px;
      color: var(--accent-gold);
      letter-spacing: 0.01em;
      margin: 0;
    }
    .product-card__cta {
      width: 100%;
      padding: 10px 14px;
      border: 1px solid var(--text-primary);
      border-radius: 0;
      font-family: 'Inter', sans-serif;
      font-size: 10px;
      font-weight: 500;
      letter-spacing: 0.22em;
      text-transform: uppercase;
      cursor: pointer;
      transition: all 0.3s ease;
      backdrop-filter: blur(8px);
    }
    .product-card__cta--primary {
      background: rgba(43,43,43,0.94);
      color: var(--bg-primary);
    }
    .product-card__cta--primary:hover {
      background: var(--accent-gold);
      border-color: var(--accent-gold);
    }
    .product-card__cta--ghost {
      background: rgba(255,254,242,0.94);
      color: var(--text-primary);
    }
    .product-card__cta--ghost:hover {
      background: var(--text-primary);
      color: var(--bg-primary);
    }
    @media (hover: none) {
      .product-card__hover {
        transform: translateY(0);
        opacity: 1;
        background: linear-gradient(to top, rgba(0,0,0,0.35), transparent 60%);
      }
    }
    .product-card--clickable { cursor: pointer; }
    .product-card--clickable:focus-visible {
      outline: 2px solid var(--accent-gold);
      outline-offset: 4px;
    }
  `;
  document.head.appendChild(css);
}

export default ProductCard;
